import React, { useState, useRef, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { RadioButton } from 'primereact/radiobutton';
import { Toast } from 'primereact/toast';
import { cancelBill, updateBillPaymentMode } from '../../utilities/api';
import './BillViewReceiptSidePanel.css';

const formatCurrency = (amount) => {
    const num = parseFloat(amount || 0);
    return num.toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 });
};

/** Format amount with rupee symbol for display */
const formatAmountWithRupee = (amount) => `₹ ${formatCurrency(amount)}`;

/**
 * Get payment options from receipt_settings: setting_type === "Payment" and setting_access === "1".
 * Excludes the current payment mode.
 */
const getPaymentOptions = (receiptSettings, currentPaymentMode) => {
    if (!receiptSettings || !Array.isArray(receiptSettings)) return [];
    return receiptSettings
        .filter((s) => (s.setting_type === 'Payment' || (s.setting_type && s.setting_type.toLowerCase() === 'payment')) && s.setting_access === '1')
        .filter((s) => {
            const name = (s.setting_name || s.payment_name || '').trim().toUpperCase();
            const current = (currentPaymentMode || '').trim().toUpperCase();
            return name && name !== current;
        })
        .map((s) => ({
            label: s.setting_name || s.payment_name || s.setting_value || 'Payment',
            value: s.setting_name || s.payment_name || s.setting_value
        }));
};

/**
 * Derive current payment mode from order_summary or order_payments.
 */
const getCurrentPaymentMode = (orderSummary, orderPayments) => {
    if (orderSummary?.payment_type) return orderSummary.payment_type;
    if (orderSummary?.mode_of_payment) return orderSummary.mode_of_payment;
    if (orderPayments && orderPayments.length > 0) {
        const first = orderPayments[0];
        return first.payment_types || first.payment_type || first.mode_of_payment || first.payment_name || first.setting_name;
    }
    return '';
};

/** Get display label for a payment row (support payment_types + payment_type, etc.) */
const getPaymentTypeLabel = (p) => p.payment_types || p.payment_type || p.mode_of_payment || p.payment_name || 'Payment';

/** Get amount for a payment row (support pay_amount + amount, paid_amount) */
const getPaymentAmount = (p) => (p.pay_amount != null ? p.pay_amount : p.amount ?? p.paid_amount);

/** Format date string (e.g. 2026-02-06 -> 06-02-2026) */
const formatDate = (dateStr) => {
    if (!dateStr || dateStr === '0') return '—';
    const parts = String(dateStr).trim().split('-');
    if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
    return dateStr;
};

const SECTIONS = {
    PAYMENT_HISTORY: 'payment_history',
    CANCEL_BILL: 'cancel_bill',
    MODIFY_PAYMENT: 'modify_payment',
    SETTLEMENT: 'settlement'
};

const TAB_ITEMS = [
    { key: 'payment_history', label: 'Payment history', icon: 'pi pi-history' },
    { key: 'cancel_bill', label: 'Cancel bill', icon: 'pi pi-times-circle' },
    { key: 'modify_payment', label: 'Modify payment', icon: 'pi pi-wallet' },
    { key: 'settlement', label: 'Settlement', icon: 'pi pi-money-bill' }
];

const cancelReasonOptions = [
    { label: 'Duplicate Item', value: 'duplicate item' },
    { label: 'Wrong Item Name', value: 'wrong item name' },
    { label: 'Item Not Available', value: 'item not available' }
];

const BillViewReceiptSidePanel = ({ billId, branchId, orderPayments = [], receiptSettings = [], orderSummary, editedBy, onBillCancelled, onPaymentModeUpdated, toastRef }) => {
    const [activeSection, setActiveSection] = useState(SECTIONS.PAYMENT_HISTORY);
    const [cancelReason, setCancelReason] = useState('');
    const [cancelling, setCancelling] = useState(false);
    const [selectedNewPayment, setSelectedNewPayment] = useState(null);
    const [updatingPayment, setUpdatingPayment] = useState(false);

    const currentPaymentMode = getCurrentPaymentMode(orderSummary, orderPayments);
    const paymentOptions = getPaymentOptions(receiptSettings, currentPaymentMode);
    const showModifyPayment = paymentOptions.length > 0;
    const billStatus = orderSummary?.status || orderSummary?.bill_status || '';
    const isCancelled = (billStatus || '').toLowerCase() === 'cancelled';
    const showCancelBill = !isCancelled;

    // If active section is cancel_bill but bill is cancelled, switch to payment_history
    useEffect(() => {
        if (activeSection === SECTIONS.CANCEL_BILL && isCancelled) {
            setActiveSection(SECTIONS.PAYMENT_HISTORY);
        }
    }, [isCancelled, activeSection]);

    const visibleTabs = TAB_ITEMS.filter((t) => {
        if (t.key === 'modify_payment') return showModifyPayment;
        if (t.key === 'cancel_bill') return showCancelBill;
        return true;
    });

    const handleCancelBill = async () => {
        if (!cancelReason) {
            toastRef?.current?.show({
                severity: 'warn',
                summary: 'Reason required',
                detail: 'Please select a reason for cancelling the bill.',
                life: 3000
            });
            return;
        }
        setCancelling(true);
        try {
            await cancelBill(billId, branchId, cancelReason, editedBy);
            toastRef?.current?.show({
                severity: 'success',
                summary: 'Bill cancelled',
                detail: 'The bill has been cancelled successfully.',
                life: 3000
            });
            setCancelReason('');
            onBillCancelled?.();
        } catch (err) {
            console.error('Error cancelling bill:', err);
            toastRef?.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: err?.response?.data?.message || err?.message || 'Failed to cancel bill',
                life: 4000
            });
        } finally {
            setCancelling(false);
        }
    };

    const handleModifyPaymentMode = async () => {
        if (!selectedNewPayment) {
            toastRef?.current?.show({
                severity: 'warn',
                summary: 'Select payment',
                detail: 'Please select a new mode of payment.',
                life: 3000
            });
            return;
        }
        setUpdatingPayment(true);
        try {
            await updateBillPaymentMode(billId, branchId, selectedNewPayment, editedBy);
            toastRef?.current?.show({
                severity: 'success',
                summary: 'Payment updated',
                detail: 'Mode of payment has been updated.',
                life: 3000
            });
            setSelectedNewPayment(null);
            onPaymentModeUpdated?.();
        } catch (err) {
            console.error('Error updating payment mode:', err);
            toastRef?.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: err?.response?.data?.message || err?.message || 'Failed to update payment mode',
                life: 4000
            });
        } finally {
            setUpdatingPayment(false);
        }
    };

    const payments = Array.isArray(orderPayments) ? orderPayments : [];

    return (
        <div className="bill-receipt-side-panel no-print">
            <div className="side-panel-tabs">
                {visibleTabs.map((tab) => (
                    <button key={tab.key} type="button" className={`side-panel-tab ${activeSection === tab.key ? 'active' : ''}`} onClick={() => setActiveSection(tab.key)}>
                        <i className={tab.icon} />
                        <span>{tab.label}</span>
                    </button>
                ))}
            </div>

            {activeSection === SECTIONS.PAYMENT_HISTORY && (
                <section className="side-panel-section side-panel-reveal">
                    <div className="payment-history-list">
                        {payments.length === 0 ? (
                            <p className="side-panel-empty">No payment records</p>
                        ) : (
                            <ul>
                                {payments.map((p, idx) => (
                                    <li key={idx} className="payment-history-item">
                                        <div className="payment-history-row payment-history-main">
                                            <span className="payment-type">{getPaymentTypeLabel(p)}</span>
                                            <span className="payment-amount">{formatAmountWithRupee(getPaymentAmount(p))}</span>
                                        </div>
                                        <div className="payment-history-row payment-history-dates">
                                            <span className="payment-date">Date: {formatDate(p.payment_date)}</span>
                                            <span className="payment-convertedate">Converted: {formatDate(p.payment_convertedate)}</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </section>
            )}

            {activeSection === SECTIONS.CANCEL_BILL && showCancelBill && (
                <section className="side-panel-section side-panel-reveal">
                    <div className="flex flex-column gap-2">
                        {cancelReasonOptions.map((option) => (
                            <div key={option.value} className="flex align-items-center gap-2">
                                <RadioButton inputId={`receipt-cancel-reason-${option.value}`} name="receiptCancelReason" value={option.value} onChange={(e) => setCancelReason(e.value)} checked={cancelReason === option.value} />
                                <label htmlFor={`receipt-cancel-reason-${option.value}`}>{option.label}</label>
                            </div>
                        ))}
                    </div>
                    <Button label="Cancel bill" icon="pi pi-ban" className="p-button-danger side-panel-btn" onClick={handleCancelBill} loading={cancelling} disabled={cancelling} />
                </section>
            )}

            {activeSection === SECTIONS.MODIFY_PAYMENT && showModifyPayment && (
                <section className="side-panel-section side-panel-reveal">
                    <p className="side-panel-hint">
                        Current: <strong>{currentPaymentMode || '—'}</strong>. Select new payment mode:
                    </p>
                    <Dropdown value={selectedNewPayment} options={paymentOptions} onChange={(e) => setSelectedNewPayment(e.value)} placeholder="Select payment mode" className="w-full payment-mode-dropdown" />
                    <Button label="Update payment mode" icon="pi pi-check" className="p-button-outlined side-panel-btn" onClick={handleModifyPaymentMode} loading={updatingPayment} disabled={updatingPayment || !selectedNewPayment} />
                </section>
            )}

            {activeSection === SECTIONS.SETTLEMENT && (
                <section className="side-panel-section side-panel-reveal">
                    <div className="settlement-wip">
                        <span className="wip-badge">WIP</span>
                        <p>Settlement option coming soon.</p>
                    </div>
                </section>
            )}
        </div>
    );
};

export default BillViewReceiptSidePanel;
